import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Check, Loader2, Zap, Crown, ShieldCheck, ArrowRight,
  BadgeCheck, RefreshCw, Gift, Copy, Users, Mail, ExternalLink,
  FileText, Receipt, Settings2,
} from "lucide-react";
import { toast } from "sonner";
import CertifiedBadge from "@/components/CertifiedBadge";
import { printCertificate } from "@/lib/certificatePdf";
import { Award, Download } from "lucide-react";

const PRO_PERKS = [
  { icon: Zap,         text: "Unlimited AI Invoice extraction" },
  { icon: Sparkles,    text: "Unlimited AI Plating Coach critiques" },
  { icon: BadgeCheck,  text: "Journal, Contributors & Press module" },
  { icon: ShieldCheck, text: "Priority support" },
];

function centsLabel(n) {
  return `$${n.toFixed(0)}`;
}

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export default function Billing() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [polling, setPolling] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState(null);
  const [prefsBusy, setPrefsBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, certRes] = await Promise.all([
        api.get("/billing/status"),
        api.get("/certification/status").catch(() => ({ data: null })),
      ]);
      setStatus(data);
      setCert(certRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
    // Invoice history loads in parallel; a missing customer id returns an
    // empty list, so no need to gate on the plan.
    setInvoicesLoading(true);
    try {
      const { data } = await api.get("/billing/invoices");
      setInvoices(data?.invoices || []);
    } catch (e) {
      /* silent: an empty invoice list is the default state */
    } finally {
      setInvoicesLoading(false);
    }
    // Load email preferences (independent of Stripe).
    try {
      const { data } = await api.get("/emails/preferences");
      setEmailPrefs(data);
    } catch (e) { /* silent */ }
  };

  const setEmailPref = async (key, value) => {
    if (!emailPrefs) return;
    const next = { ...emailPrefs, [key]: value };
    setEmailPrefs(next); // optimistic
    setPrefsBusy(true);
    try {
      const { data } = await api.patch("/emails/preferences", next);
      setEmailPrefs(data);
      toast.success(value ? "Subscribed to " + key.replace("_", " ") + " email." : "Unsubscribed from " + key.replace("_", " ") + " email.");
    } catch (e) {
      setEmailPrefs(emailPrefs); // revert
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPrefsBusy(false);
    }
  };

  const openCustomerPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open Stripe billing portal.");
    } finally {
      setPortalBusy(false);
    }
  };

  // Poll checkout status when returning from Stripe (?session_id=xxx&status=success).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    const backStatus = params.get("status");
    if (sessionId && backStatus === "success") {
      setPolling(true);
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/billing/session/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success("Welcome to Pro. Every feature is on.");
            await refresh();
            await load();
            setPolling(false);
            // Strip query params.
            navigate("/billing", { replace: true });
            return;
          }
          if (data.status === "expired") {
            toast.error("That checkout session expired. Please try again.");
            setPolling(false);
            navigate("/billing", { replace: true });
            return;
          }
        } catch (e) {
          /* keep polling */
        }
        if (attempts < 8) setTimeout(poll, 2000);
        else {
          setPolling(false);
          toast.info("Payment is still processing. Refresh in a moment.");
        }
      };
      poll();
    } else if (backStatus === "cancel") {
      toast.info("Checkout cancelled. You can pick up any time.");
      navigate("/billing", { replace: true });
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const isPro = status?.plan === "pro";
  const isTrialing = status?.plan_status === "trialing";
  const canStartTrial = status?.can_start_trial;
  const trialDaysLeft = daysUntil(status?.trial_ends_at);
  const periodDaysLeft = daysUntil(status?.current_period_end);

  const startTrial = async () => {
    setBusy("trial");
    try {
      const { data } = await api.post("/billing/trial/start");
      setStatus(data);
      await refresh();
      toast.success("Your 14-day Pro trial is live. Enjoy.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const checkout = async (plan) => {
    setBusy(plan);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", { plan, origin_url: origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy("");
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Cancel your Pro subscription? You will keep Pro access until the end of the current billing period, then it will lapse. You can reactivate at any time before then.")) return;
    setBusy("cancel");
    try {
      const { data } = await api.post("/billing/cancel");
      setStatus(data);
      toast.success("Cancellation scheduled. You keep Pro until the current period ends.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const reactivateSubscription = async () => {
    setBusy("reactivate");
    try {
      const { data } = await api.post("/billing/reactivate");
      setStatus(data);
      toast.success("Your subscription is back on. Next renewal will proceed as normal.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const monthly = status?.prices?.monthly;
  const yearly = status?.prices?.yearly;

  const yearlySavingsPct = useMemo(() => {
    if (!monthly?.amount || !yearly?.amount) return 0;
    const yearlyAsMonthly = monthly.amount * 12;
    return Math.round(((yearlyAsMonthly - yearly.amount) / yearlyAsMonthly) * 100);
  }, [monthly, yearly]);

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex justify-center items-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-5xl mx-auto space-y-8" data-testid="billing-page">
      <header>
        <div className="label-eyebrow flex items-center gap-2">
          <Crown className="w-3 h-3" style={{ color: "#f2dfb5" }} /> Billing
        </div>
        <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">
          {isPro ? "You're on Pro." : "Reclaim every gram of margin."}
        </h1>
        <p className="text-muted-foreground mt-3 text-sm max-w-2xl">
          {isPro
            ? "Every AI feature, every module, uncapped. Thank you for supporting the kitchen."
            : "Free stays generous. Pro turns on the AI features that pay for themselves in one invoice."}
        </p>
      </header>

      {polling && (
        <div
          className="glass-card rounded-2xl p-4 flex items-center gap-3 text-sm"
          data-testid="billing-polling"
        >
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#f2dfb5" }} />
          Confirming your payment with Stripe…
        </div>
      )}

      {/* Current plan card */}
      <section className="glass-card rounded-2xl p-6" data-testid="billing-current-plan">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="label-eyebrow text-[0.55rem]">Current plan</div>
            <div className="font-display text-2xl mt-1 flex items-center gap-2">
              {isPro ? (
                <>
                  <Crown className="w-5 h-5" style={{ color: "#f2dfb5" }} />
                  Pro
                  {isTrialing && (
                    <span className="text-xs font-normal text-white/70 ml-2">
                      Trial • {trialDaysLeft ?? 0} day{trialDaysLeft === 1 ? "" : "s"} left
                    </span>
                  )}
                  {!isTrialing && periodDaysLeft != null && (
                    <span className="text-xs font-normal text-white/70 ml-2">
                      Renews in {periodDaysLeft} day{periodDaysLeft === 1 ? "" : "s"}
                    </span>
                  )}
                </>
              ) : (
                <>
                  Free
                </>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Signed in as <span className="text-foreground">{user?.email}</span>
            </div>
          </div>

          {!isPro && canStartTrial && (
            <Button
              onClick={startTrial}
              disabled={busy === "trial"}
              className="gold-cta group h-10 px-4 gap-2"
              data-testid="start-trial-btn"
            >
              {busy === "trial" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Start 14-day free trial
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </Button>
          )}

          {isPro && (
            <Button
              variant="outline"
              onClick={load}
              className="gap-2"
              data-testid="refresh-status-btn"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
          )}
        </div>
      </section>

      {/* Certified Culinary Specialist */}
      <section className="glass-card rounded-2xl p-6" data-testid="billing-certification">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="label-eyebrow text-[0.55rem] flex items-center gap-1.5">
              <Award className="w-3 h-3" style={{ color: "#f2dfb5" }} /> Certified Culinary Specialist
            </div>
            {cert?.certified ? (
              <>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <span className="font-display text-2xl">Certified.</span>
                  <CertifiedBadge size="md" certificateNumber={cert.certificate_number} />
                </div>
                <p className="text-xs text-muted-foreground mt-2 max-w-lg">
                  Awarded {new Date(cert.certified_at).toLocaleDateString()}. Reprint the certificate anytime for kitchen walls, staff onboarding or PR features.
                </p>
              </>
            ) : (
              <>
                <div className="mt-2 font-display text-2xl">
                  {cert?.quiz_passed_at ? "Nearly there." : "Prove your precision."}
                </div>
                <p className="text-xs text-muted-foreground mt-2 max-w-lg">
                  A 3-question quiz plus a live Recipe-to-Inventory link ({cert?.actions_done ?? 0}/3 done). Once earned, the badge shows on your sidebar, your billing page and any public journal profile. It's a mark that your kitchen data is trustworthy.
                </p>
              </>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            {cert?.certified ? (
              <Button
                variant="outline"
                onClick={() => printCertificate({
                  name: user?.name, email: user?.email,
                  certificateNumber: cert.certificate_number, certifiedAt: cert.certified_at,
                })}
                className="gap-2"
                data-testid="billing-print-cert"
              >
                <Download className="w-4 h-4" /> Print certificate
              </Button>
            ) : (
              <div className="text-[0.65rem] text-muted-foreground">
                Open the concierge (bottom-right) to begin.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Pricing tiers */}
      {(!isPro || isTrialing) && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="billing-plans">
          {/* Monthly */}
          <div className="glass-card rounded-2xl p-6 flex flex-col" data-testid="plan-monthly">
            <div className="label-eyebrow text-[0.55rem]">Pro • Monthly</div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-display text-5xl leading-none">
                {monthly ? centsLabel(monthly.amount) : "-"}
              </span>
              <span className="text-sm text-muted-foreground">/ 30 days</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Non-recurring. Renew when you like.</p>
            <ul className="mt-6 space-y-2 text-sm">
              {PRO_PERKS.map((p) => (
                <li key={p.text} className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#f2dfb5" }} />
                  <span>{p.text}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 mt-auto pt-6">
              <Button
                onClick={() => checkout("monthly")}
                disabled={busy === "monthly"}
                className="gold-cta w-full h-11 gap-2"
                data-testid="checkout-monthly"
              >
                {busy === "monthly" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {isTrialing ? "Upgrade now" : "Get Pro"}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Yearly */}
          <div
            className="glass-card rounded-2xl p-6 flex flex-col relative overflow-hidden"
            style={{
              boxShadow: "0 0 0 1px rgba(232,207,154,0.35), 0 12px 40px -8px rgba(200,169,106,0.25)",
            }}
            data-testid="plan-yearly"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="label-eyebrow text-[0.55rem]">Pro • Yearly</div>
              <div className="text-[0.55rem] uppercase tracking-[0.24em] px-2 py-1 rounded-full shrink-0"
                style={{
                  background: "linear-gradient(180deg, rgba(232,207,154,0.30), rgba(232,207,154,0.10))",
                  border: "1px solid rgba(232,207,154,0.45)",
                  color: "#f2dfb5",
                }}
              >
                Best value
              </div>
            </div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-display text-5xl leading-none">
                {yearly ? centsLabel(yearly.amount) : "-"}
              </span>
              <span className="text-sm text-muted-foreground">/ year</span>
            </div>
            <p className="text-xs mt-2" style={{ color: "rgba(232,207,154,0.85)" }}>
              {yearlySavingsPct > 0 ? `Save ${yearlySavingsPct}% vs monthly: about 2 months free.` : "One-shot annual plan"}
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              {PRO_PERKS.map((p) => (
                <li key={p.text} className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#f2dfb5" }} />
                  <span>{p.text}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 mt-auto pt-6">
              <Button
                onClick={() => checkout("yearly")}
                disabled={busy === "yearly"}
                className="gold-cta w-full h-11 gap-2"
                data-testid="checkout-yearly"
              >
                {busy === "yearly" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Get Pro • Yearly
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </section>
      )}

      {isPro && !isTrialing && (
        <section className="glass-card rounded-2xl p-6" data-testid="billing-renew">
          <div className="label-eyebrow text-[0.55rem]">Extend Pro</div>
          <p className="text-sm text-muted-foreground mt-2">
            Add another period to your Pro access whenever you like. Your existing time isn't lost: new days are appended to your current period.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <Button
              onClick={() => checkout("monthly")}
              disabled={busy === "monthly"}
              variant="outline"
              className="h-11 gap-2"
              data-testid="renew-monthly"
            >
              {busy === "monthly" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>+30 days • {centsLabel(monthly?.amount ?? 29)}</>}
            </Button>
            <Button
              onClick={() => checkout("yearly")}
              disabled={busy === "yearly"}
              className="gold-cta h-11 gap-2"
              data-testid="renew-yearly"
            >
              {busy === "yearly" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>+1 year • {centsLabel(yearly?.amount ?? 290)}</>}
            </Button>
          </div>
        </section>
      )}

      {/* Weekly Cost Digest: Pro only */}
      {isPro && (
        <section className="glass-card rounded-2xl p-6" data-testid="weekly-digest">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="label-eyebrow text-[0.55rem] flex items-center gap-1.5">
                <Mail className="w-3 h-3" style={{ color: "#f2dfb5" }} /> Weekly cost digest
              </div>
              <h3 className="font-display text-xl mt-1 text-white/95">
                Sunday-night snapshot delivered to your inbox.
              </h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-lg">
                Every Sunday you'll get a beautifully-formatted summary of last week's revenue, food-cost %, portions sold, top dishes and labour billed. Reply to opt out any time.
              </p>
            </div>
            <Button
              variant="outline"
              className="gap-1.5 shrink-0"
              onClick={() => {
                const origin = window.location.origin;
                window.open(`${origin.replace(":3000", "")}/api/billing/weekly-digest/preview?format=html`, "_blank");
              }}
              data-testid="digest-preview-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Preview my digest
            </Button>
          </div>
        </section>
      )}

      {/* Invoices & Receipts */}
      <section className="glass-card rounded-2xl p-6" data-testid="invoices-receipts">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="min-w-0 flex-1">
            <div className="label-eyebrow text-[0.55rem] flex items-center gap-1.5">
              <Receipt className="w-3 h-3" style={{ color: "#f2dfb5" }} /> Invoices and receipts
            </div>
            <h3 className="font-display text-xl mt-1 text-white/95">Every charge, every PDF, one click away.</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-lg">
              Invoices are issued by Stripe on every renewal. Download a stamped PDF or the payment receipt for accounting.
            </p>
          </div>
          <Button
            onClick={openCustomerPortal}
            disabled={portalBusy || !status?.plan || status.plan === "free"}
            className="gap-2"
            variant="outline"
            data-testid="open-customer-portal"
          >
            {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings2 className="w-4 h-4" />}
            Manage billing
          </Button>
        </div>

        {invoicesLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="invoices-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading invoices from Stripe...
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-sm text-muted-foreground py-3" data-testid="invoices-empty">
            No invoices yet. Your first Stripe invoice arrives the moment your subscription starts.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="invoices-list">
            <table className="w-full text-sm tabular">
              <thead>
                <tr className="text-[0.6rem] uppercase tracking-[0.24em] text-white/50">
                  <th className="text-left py-2 pr-4">Date</th>
                  <th className="text-left py-2 pr-4">Invoice</th>
                  <th className="text-left py-2 pr-4">Status</th>
                  <th className="text-right py-2 pr-4">Amount</th>
                  <th className="text-right py-2">Files</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`invoice-row-${inv.id}`}>
                    <td className="py-2 pr-4 text-white/80">
                      {inv.created ? new Date(inv.created).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : ""}
                    </td>
                    <td className="py-2 pr-4 text-white/70">
                      {inv.number || inv.id.slice(-8)}
                      {inv.description ? <div className="text-[0.65rem] text-white/40 mt-0.5 truncate max-w-[240px]">{inv.description}</div> : null}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className="px-2 py-0.5 rounded-full text-[0.6rem] uppercase tracking-[0.14em]"
                        style={{
                          background: inv.status === "paid" ? "rgba(141,211,164,0.08)" : "rgba(232,162,75,0.08)",
                          color: inv.status === "paid" ? "#8dd3a4" : "#e8a24b",
                          border: `1px solid ${inv.status === "paid" ? "rgba(141,211,164,0.28)" : "rgba(232,162,75,0.28)"}`,
                        }}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-right text-white/85">
                      {inv.currency} {inv.amount_paid.toFixed(2)}
                    </td>
                    <td className="py-2 text-right">
                      <div className="inline-flex items-center gap-2">
                        {inv.invoice_pdf && (
                          <a
                            href={inv.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs hover:gold transition-colors"
                            data-testid={`invoice-pdf-${inv.id}`}
                          >
                            <FileText className="w-3.5 h-3.5" /> Invoice
                          </a>
                        )}
                        {inv.receipt_url && (
                          <a
                            href={inv.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs hover:gold transition-colors"
                            data-testid={`invoice-receipt-${inv.id}`}
                          >
                            <Receipt className="w-3.5 h-3.5" /> Receipt
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Subscription management: cancel + reactivate */}
      {status?.plan === "pro" && status?.plan_status === "active" && (
        <section className="glass-card rounded-2xl p-6" data-testid="subscription-management">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="label-eyebrow text-[0.55rem] flex items-center gap-1.5">
                <RefreshCw className="w-3 h-3" style={{ color: "#f2dfb5" }} /> Subscription
              </div>
              {status.cancel_at_period_end ? (
                <>
                  <h3 className="font-display text-xl mt-1 text-white/95">Cancellation scheduled.</h3>
                  <p className="text-xs text-muted-foreground mt-1 max-w-lg">
                    You keep Pro until {status.current_period_end ? new Date(status.current_period_end).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "the current period ends"}. After that your account returns to Free. You can change your mind any time before then.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-display text-xl mt-1 text-white/95">Change your mind, any time.</h3>
                  <p className="text-xs text-muted-foreground mt-1 max-w-lg">
                    Cancel keeps you on Pro until the end of your current billing period, then your card is no longer charged. No lock-in.
                  </p>
                </>
              )}
            </div>
            {status.cancel_at_period_end ? (
              <Button
                onClick={reactivateSubscription}
                disabled={busy === "reactivate"}
                className="gap-2"
                data-testid="reactivate-subscription"
              >
                {busy === "reactivate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Reactivate subscription
              </Button>
            ) : (
              <Button
                onClick={cancelSubscription}
                disabled={busy === "cancel"}
                variant="outline"
                className="gap-2"
                data-testid="cancel-subscription"
              >
                {busy === "cancel" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Cancel subscription
              </Button>
            )}
          </div>
        </section>
      )}

      {/* Email preferences */}
      {emailPrefs && (
        <section className="glass-card rounded-2xl p-6" data-testid="email-preferences">
          <div className="mb-4">
            <div className="label-eyebrow text-[0.55rem] flex items-center gap-1.5">
              <Mail className="w-3 h-3" style={{ color: "#f2dfb5" }} /> Email preferences
            </div>
            <h3 className="font-display text-xl mt-1 text-white/95">Choose what lands in your inbox.</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-lg">
              Transactional email (receipts, invoices, password resets, security alerts) is always delivered. Everything below is optional. Every marketing email we send also carries a one-click unsubscribe link.
            </p>
          </div>
          <div className="space-y-2">
            <label className="flex items-start justify-between gap-4 py-2 border-t border-white/5" data-testid="pref-marketing">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">Product updates and offers</div>
                <div className="text-xs text-muted-foreground mt-0.5">New features, promotional pricing, and platform announcements.</div>
              </div>
              <input
                type="checkbox"
                className="mt-1 w-4 h-4 accent-white"
                checked={emailPrefs.marketing}
                disabled={prefsBusy}
                onChange={(e) => setEmailPref("marketing", e.target.checked)}
                data-testid="pref-marketing-checkbox"
              />
            </label>
            <label className="flex items-start justify-between gap-4 py-2 border-t border-white/5" data-testid="pref-digest">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">Weekly digest</div>
                <div className="text-xs text-muted-foreground mt-0.5">Monday morning summary: last week's roster hours, labor cost, and margin snapshot as an Excel workbook.</div>
              </div>
              <input
                type="checkbox"
                className="mt-1 w-4 h-4 accent-white"
                checked={emailPrefs.weekly_digest}
                disabled={prefsBusy}
                onChange={(e) => setEmailPref("weekly_digest", e.target.checked)}
                data-testid="pref-digest-checkbox"
              />
            </label>
          </div>
        </section>
      )}

      {/* Referral Rewards */}
      {status?.referral_code && (
        <section className="glass-card rounded-2xl p-6" data-testid="referral-rewards">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="label-eyebrow text-[0.55rem] flex items-center gap-1.5">
                <Gift className="w-3 h-3" style={{ color: "#f2dfb5" }} /> Referral rewards
              </div>
              <h3 className="font-display text-xl mt-1 text-white/95">
                Get {status.referral_reward_days} free days for every friend who subscribes.
              </h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-lg">
                Share your link with another kitchen. When they upgrade to Pro, we top up your account with an extra {status.referral_reward_days} days: automatically, on top of whatever time you have left.
              </p>
            </div>
            <div className="flex gap-3 text-right shrink-0">
              <div className="rounded-lg px-3 py-2" style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                minWidth: 88,
              }}>
                <div className="text-[0.55rem] uppercase tracking-[0.24em] text-white/50 flex items-center gap-1 justify-end">
                  <Users className="w-3 h-3" /> Referred
                </div>
                <div className="font-display text-2xl tabular-nums mt-0.5" data-testid="referral-count">
                  {status.successful_referrals || 0}
                </div>
              </div>
              <div className="rounded-lg px-3 py-2" style={{
                background: "linear-gradient(180deg, rgba(232,207,154,0.14), rgba(232,207,154,0.03))",
                border: "1px solid rgba(232,207,154,0.28)",
                minWidth: 88,
              }}>
                <div className="text-[0.55rem] uppercase tracking-[0.24em]" style={{ color: "rgba(232,207,154,0.85)" }}>
                  Days earned
                </div>
                <div className="font-display text-2xl tabular-nums mt-0.5" style={{ color: "#f2dfb5" }} data-testid="referral-days-earned">
                  {status.referral_days_earned || 0}
                </div>
              </div>
            </div>
          </div>

          <div
            className="mt-5 rounded-lg flex items-center gap-2 p-1 pl-3"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <input
              readOnly
              value={status.referral_share_url || ""}
              className="flex-1 bg-transparent text-sm text-white/85 min-w-0 outline-none tabular-nums"
              onFocus={(e) => e.target.select()}
              data-testid="referral-share-url"
            />
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 shrink-0"
              onClick={() => {
                if (!status.referral_share_url) return;
                navigator.clipboard.writeText(status.referral_share_url);
                toast.success("Referral link copied.");
              }}
              data-testid="referral-copy-btn"
            >
              <Copy className="w-3.5 h-3.5" /> Copy
            </Button>
            <Button
              size="sm"
              className="gold-cta gap-1.5 shrink-0"
              onClick={() => {
                if (!status.referral_share_url) return;
                const text = `Try CulinaireSpecialist, the precision cost engine for restaurants. Free 14-day trial, no card required. ${status.referral_share_url}`;
                if (navigator.share) {
                  navigator.share({ title: "CulinaireSpecialist", text, url: status.referral_share_url }).catch(() => {});
                } else {
                  navigator.clipboard.writeText(text);
                  toast.success("Share message copied.");
                }
              }}
              data-testid="referral-share-btn"
            >
              <Gift className="w-3.5 h-3.5" /> Share
            </Button>
          </div>
          <div className="mt-2 text-[0.65rem] text-white/40 flex flex-wrap gap-2">
            <span>Your code: <span className="tabular-nums font-mono" style={{ color: "rgba(232,207,154,0.85)" }}>{status.referral_code}</span></span>
            <span className="text-white/25">•</span>
            <span>No cap on referrals. Only counts when they pay for their first Pro period.</span>
          </div>
        </section>
      )}

      <p className="text-xs text-muted-foreground text-center pt-4">
        Payments processed securely by Stripe. Prices in USD. No auto-renewal: you're always in control.
      </p>
    </div>
  );
}
